/**
 * Comparaison du code **encore dans le formulaire** avec une version déjà
 * déployée (§3.5).
 *
 * Le comparateur de la fiche application ne sait confronter que deux versions
 * existantes : il ne répond donc qu'après coup, une fois la mise à jour partie.
 * La question utile se pose avant — « qu'est-ce que je change au juste ? » — et
 * c'est celle-ci qui évite d'écraser une application en ligne par un artifact
 * qu'on croyait à jour.
 *
 * Le code comparé est celui **avant transformation** des deux côtés : le
 * brouillon n'a pas encore été réécrit par l'IA, et le confronter au code
 * transformé d'une version reviendrait à imputer à l'auteur des différences qui
 * ne sont pas de lui.
 */

import type { AppVersionDto } from '@portail/shared';
import { ChevronDown, ChevronUp, GitCompare } from 'lucide-react';
import { useId, useState } from 'react';

import { getVersionCode } from '../api';
import { useAsync } from '../hooks/useAsync';
import { ArtifactPreview } from './ArtifactPreview';
import { CodeDiff } from './CodeDiff';
import { Button, ErrorBanner, Label, Select, Skeleton } from './ui';
import { versionLabel } from './VersionHistory';
import { ViewSwitch, type ComparisonView } from './ViewSwitch';

export interface DraftComparisonProps {
  appId: string;
  /** Code saisi dans le formulaire, tel quel. */
  draft: string;
  /** Versions de l'application, du plus récent au plus ancien. */
  versions: AppVersionDto[];
  /** Version proposée par défaut : celle en ligne. */
  defaultVersionId: string | null;
}

/** Volet dépliable comparant le brouillon à une version au choix. */
export function DraftComparison({
  appId,
  draft,
  versions,
  defaultVersionId,
}: DraftComparisonProps) {
  const selectId = useId();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<ComparisonView>('visual');
  const [versionId, setVersionId] = useState(defaultVersionId ?? versions[0]?.id ?? '');

  const empty = draft.trim().length === 0;

  /**
   * Le code de la version n'est demandé que le volet ouvert : sur un formulaire
   * que l'on remplit sans jamais comparer, aucun appel ne part.
   */
  const version = useAsync(
    () => (open && versionId ? getVersionCode(appId, versionId) : Promise.resolve(null)),
    [appId, versionId, open],
  );

  if (versions.length === 0) return null;

  return (
    <section className="rounded-md border border-slate-200 dark:border-slate-800">
      <h3>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-slate-900 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:text-slate-100 dark:hover:bg-slate-950"
        >
          {open ? (
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          )}
          <GitCompare className="h-4 w-4" aria-hidden="true" />
          Comparer avec une version déjà déployée
          <span className="ml-auto text-xs font-normal text-slate-500 dark:text-slate-400">
            facultatif
          </span>
        </button>
      </h3>

      {open ? (
        <div className="space-y-4 border-t border-slate-200 p-4 dark:border-slate-800">
          {empty ? (
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Collez d'abord votre nouveau code : il n'y a rien à comparer pour l'instant.
            </p>
          ) : (
            <>
              <div>
                <Label htmlFor={selectId}>Comparer à</Label>
                <Select
                  id={selectId}
                  value={versionId}
                  onChange={(event) => setVersionId(event.target.value)}
                >
                  {versions.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {versionLabel(entry)}
                    </option>
                  ))}
                </Select>
              </div>

              <ViewSwitch value={view} onChange={setView} />

              <p className="text-xs text-slate-500 dark:text-slate-400">
                Les deux côtés sont montrés <strong>avant</strong> adaptation par l'IA : votre
                brouillon ne l'a pas encore subie, et la comparer au résultat transformé d'une
                version afficherait des différences qui ne viennent pas de vous.
              </p>

              {version.loading ? (
                <Skeleton className="h-64 w-full" />
              ) : version.error ? (
                <ErrorBanner message={version.error} onRetry={version.reload} />
              ) : version.data ? (
                view === 'visual' ? (
                  <div className="grid gap-4 lg:grid-cols-2">
                    <ArtifactPreview
                      code={version.data.originalCode}
                      label={`v${version.data.versionNumber} — en ligne`}
                    />
                    <ArtifactPreview code={draft} label="Votre nouveau code" />
                  </div>
                ) : (
                  <CodeDiff
                    baseCode={version.data.originalCode}
                    targetCode={draft}
                    baseLabel={`v${version.data.versionNumber}`}
                    targetLabel="Votre nouveau code"
                    identicalMessage={`Votre code est rigoureusement identique à celui de la v${version.data.versionNumber}.`}
                  />
                )
              ) : null}
            </>
          )}

          <Button variant="ghost" onClick={() => setOpen(false)}>
            Replier
          </Button>
        </div>
      ) : null}
    </section>
  );
}
